import type { MeshToonMaterial } from 'three';
import type { CarModel } from './carModel';
import { type ContactShadow, makeContactShadow } from './contactShadow';

/** What the renderer knows about a car this frame (three.js frame: x, z; yaw about +y). */
export interface CarVisual {
  x: number;
  z: number;
  /** Road height under the car, and its nose-up slope angle (radians). */
  y: number;
  pitch: number;
  /** Roll with the road's banking (radians, left side up positive). */
  roll?: number;
  yaw: number;
  wheelAngle: number;
  /** Forward speed, m/s. */
  speed: number;
  /** Longitudinal / lateral acceleration, m/s^2 (lateral: left positive). */
  ax: number;
  ay: number;
  /** Bit per wheel on a curb (FL, FR, RL, RR). */
  curbMask: number;
  /** Wheels on grass or gravel. */
  offWheels: number;
  impact: number;
}

class Spring {
  x = 0;
  v = 0;
  constructor(private k: number, private c: number) {}
  step(target: number, dt: number): number {
    const a = this.k * (target - this.x) - this.c * this.v;
    this.v += a * dt;
    this.x += this.v * dt;
    return this.x;
  }
  kick(dv: number): void {
    this.v += dv;
  }
}

/** How high the body's lowest point always stays above the road, m. */
const CLEARANCE = 0.015;

/**
 * Squash and stretch, bounces and the helmet bobble: purely visual springs on
 * top of the simulated pose (render-rate, not part of the deterministic sim).
 */
export class CarRig {
  private stretch = new Spring(60, 9);
  private roll = new Spring(55, 8);
  private bounce = new Spring(160, 9);
  private tilt = new Spring(120, 10);
  private headX = new Spring(90, 7);
  private headY = new Spring(120, 8);
  private spinAngle = [0, 0, 0, 0];
  private t = 0;
  private curbPhase = 0;

  /** The dark patch on the road under the car (see contactShadow.ts). */
  private contact: ContactShadow;

  /** Where the body squats (the rear axle) and dives (the front one). */
  private rearX: number;
  private frontX: number;

  constructor(readonly model: CarModel) {
    this.contact = makeContactShadow(model);
    model.root.add(this.contact.mesh);
    this.rearX = model.wheels.find((w) => !w.front)?.steer.position.x ?? 0;
    this.frontX = model.wheels.find((w) => w.front)?.steer.position.x ?? 0;
  }

  update(s: CarVisual, dt: number): void {
    const m = this.model;
    const h = Math.min(dt, 1 / 30);
    this.t += h;
    m.root.position.set(s.x, s.y, s.z);
    m.root.rotation.set(s.roll ?? 0, s.yaw, s.pitch, 'YZX');

    // Stretch under acceleration, squash under braking.
    const st = this.stretch.step(Math.max(-1, Math.min(1, s.ax / 9)), h);
    const roll = this.roll.step(Math.max(-0.085, Math.min(0.085, s.ay * 0.006)), h);

    // Curbs: a light rattle of small hops (a little more when the front wheels hit), at most 14 a second.
    const onCurb = s.curbMask !== 0;
    if (onCurb && s.speed > 3) {
      this.curbPhase += h * Math.min(s.speed * 0.9, 14);
      if (this.curbPhase > 1) {
        this.curbPhase -= 1;
        const front = (s.curbMask & 3) !== 0;
        this.bounce.kick((front ? 0.42 : 0.28) + Math.random() * 0.14);
        this.tilt.kick(((s.curbMask & 5) !== 0 ? 1 : -1) * (0.32 + Math.random() * 0.18));
      }
    }
    if (s.offWheels > 0 && s.speed > 4 && Math.random() < h * 14) this.bounce.kick(0.35 + Math.random() * 0.35);
    if (s.impact > 1) {
      this.bounce.kick(Math.min(3, s.impact * 0.25));
      this.tilt.kick((Math.random() - 0.5) * 4);
    }
    const hop = Math.max(-0.05, this.bounce.step(0, h));
    const tilt = this.tilt.step(0, h);

    const c = m.chassis;
    const sx = 1 + 0.08 * st;
    const sy = st < 0 ? 1 + 0.07 * st : 1 - 0.035 * st;
    const sz = st < 0 ? 1 - 0.06 * st : 1 - 0.02 * st;
    c.scale.set(sx, sy, sz);
    // It squats on its rear wheels and dives on its front ones, so the end nearest the road hardly drops (leaning
    // about the middle of the car, a long tail or a wide front wing swung down into it).
    const pitch = st * 0.04;
    const px = (st > 0 ? this.rearX : this.frontX) * sx;
    c.position.set(-0.05 * st + px - px * Math.cos(pitch), hop * 0.12 - px * Math.sin(pitch), 0);
    c.rotation.set(roll + tilt * 0.035, 0, pitch + hop * 0.05, 'YXZ');
    // What still reaches the road (a front wing tip, braking hard into a corner), the body rides up on.
    c.updateMatrix();
    const e = c.matrix.elements;
    let low = Infinity;
    for (const p of m.lows) low = Math.min(low, e[1] * p.x + e[5] * p.y + e[9] * p.z + e[13]);
    if (low < CLEARANCE) c.position.y += CLEARANCE - low;

    // The big head lags behind accelerations and bobs with the bumps.
    const hx = this.headX.step(-s.ax * 0.006, h);
    const hy = this.headY.step(hop * 0.05, h);
    m.head.position.set(m.headBase.x + hx, m.headBase.y + hy, -roll * 0.6);
    m.head.rotation.set(-roll * 1.5, 0, -hx * 1.2);

    // Wheels: spin with speed, fronts steer.
    m.wheels.forEach((w, i) => {
      this.spinAngle[i] -= (s.speed * h) / w.radius;
      w.spin.rotation.z = this.spinAngle[i];
      if (w.front) w.steer.rotation.y = s.wheelAngle;
    });

    m.scarf?.update(h, s.speed);
  }

  setOpacity(o: number): void {
    // A ghost's patch is fainter than the car itself: it's barely there.
    this.contact.setOpacity(o * 0.5);
    for (const mat of this.model.materials) {
      (mat as MeshToonMaterial).opacity = o;
      mat.depthWrite = o > 0.97;
    }
  }
}
